// src/components/CaseView/CallNudgeOverlay.tsx
// ──────────────────────────────────────────────────────────────
// Quiet tactical cue during P2P calls.
// Appears as a small unobtrusive overlay, fades in subtly,
// auto-dismisses unless hovered. Never blocks the view.
// Feels like a colleague tapping the chart.
// ──────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from "react";
import type { Nudge } from "../../hooks/useCallNudge";

interface CallNudgeOverlayProps {
  nudge: Nudge | null;
  onDismiss: () => void;
}

// ── Nudge type icons (subtle, single-character) ──
const NUDGE_ICONS: Record<string, string> = {
  guideline_misapplication: "§",
  authority_barrier: "↗",
  non_clinical_denial: "⊘",
  missing_criteria: "?",
  moving_goalposts: "…",
  wrong_frame: "↻",
  time_pressure_close: "◷",
};

export function CallNudgeOverlay({ nudge, onDismiss }: CallNudgeOverlayProps) {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTimestamp = useRef<number>(0);

  // Trigger fade-in when a new nudge arrives
  useEffect(() => {
    if (nudge && nudge.timestamp !== prevTimestamp.current) {
      prevTimestamp.current = nudge.timestamp;
      // Trigger immediately
      requestAnimationFrame(() => setVisible(true));
    }
    if (!nudge) {
      setVisible(false);
    }
  }, [nudge]);

  // Auto-dismiss fade-out (pause while hovered)
  useEffect(() => {
    if (!nudge || !visible) return;

    if (hovered) {
      // Clear any pending dismiss while hovered
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      return;
    }

    fadeTimerRef.current = setTimeout(() => {
      setVisible(false);
      // Let the CSS transition finish before calling onDismiss
      setTimeout(onDismiss, 300);
    }, 6000); // 6s visible before fade

    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [nudge, visible, hovered, onDismiss]);

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);
  const handleClick = useCallback(() => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  }, [onDismiss]);

  if (!nudge) return null;

  const icon = NUDGE_ICONS[nudge.type] || "·";

  return (
    <div
      className={`
        fixed bottom-24 right-6 z-30
        max-w-[360px]
        transition-all duration-300 ease-out
        ${visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2 pointer-events-none"
        }
      `}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3 px-4 py-3.5 rounded-xl bg-slate-800/95 border border-slate-600/50 border-l-[3px] border-l-teal-500/70 shadow-xl shadow-black/30 backdrop-blur-sm cursor-default">
        {/* Type indicator */}
        <span className="text-sm text-teal-400/80 font-mono leading-none mt-0.5 flex-shrink-0 w-5 text-center">
          {icon}
        </span>

        {/* Cue text */}
        <span className="text-[15px] text-slate-200 font-medium leading-snug">
          {nudge.cue}
        </span>
      </div>
    </div>
  );
}